import { m as motion } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { getCommonPageProps } from '@/shared/api';
import { getCarAccreditation } from '@/shared/api/car-accreditation';
import { getEmployees } from '@/shared/api/employees';
import { getPageDetail } from '@/shared/api/page-detail';
import { getSchemes } from '@/shared/api/schemes';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import AdditionalInfoSection from '@/shared/components/sections/AdditionalInfoSection';
import { AdditionalInfo } from '@/shared/components/sections/AdditionalInfoSection/AdditionalInfoSection';
import SchemaSection, { SchemaSectionProps } from '@/shared/components/sections/SchemaSection';
import ServiceCarAccreditation, {
    ServiceCarAccreditationProps,
} from '@/shared/components/sections/ServiceCarAccreditation';
import ServiceHeaderSection from '@/shared/components/sections/ServiceHeaderSection';
import ServicesContentHeader, {
    ServicesContentHeaderProps,
} from '@/shared/components/sections/ServicesContentHeader/ServicesContentHeader';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const ServicesPage = ({
    h1,
    pageTitle,
    titleDescription,
    bodyClass,
    breadcrumbs,
    meta,
    navigation,
    additionalInfoProps,
    servicesContentHeaderProps,
    carAccreditationContentProps,
    schemaSectionProps,
    buttonText,
    buttonLink,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);

    return (
        <DefaultLayout currentPageId={PAGES.CAR_ACCREDITATION} bodyClass={bodyClass} ref={ref}>
            <h1 className="visually-hidden">{h1}</h1>
            <ServiceHeaderSection
                serviceHeaderSectionProps={{
                    breadcrumbs,
                    h1: pageTitle,
                    titleDescription,
                    navigation,
                    buttonText,
                    buttonLink,
                }}
            />
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="services-wrapper"
            >
                <ServicesContentHeader servicesContentHeader={servicesContentHeaderProps} />
                <ServiceCarAccreditation carAccreditation={carAccreditationContentProps} />
                <SchemaSection {...schemaSectionProps} />
                <AdditionalInfoSection variant="medium" additionalInfoSectionProps={additionalInfoProps} />
            </motion.div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-car-accreditation"
                data={generateWebPageJsonLd({
                    name: pageTitle || h1,
                    description: titleDescription || meta.description,
                    pagePath: '/services/car-accreditation/',
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default ServicesPage;

type ServicesPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        pageTitle: string;
        navigation: AppNavigation;
        titleDescription: string;
        buttonText: string;
        buttonLink: string;
        servicesContentHeaderProps: ServicesContentHeaderProps;
        carAccreditationContentProps: ServiceCarAccreditationProps;
        schemaSectionProps: SchemaSectionProps;
        additionalInfoProps: AdditionalInfo;
    }
>;

export const getStaticProps: GetStaticProps<ServicesPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'car-accreditation' } });

    const [commonPageProps, siteSettings, employeesResponse, carAccreditationResponse, schemesResponse] =
        await Promise.all([
            getCommonPageProps(pageResponse.data?.id ?? PAGES.CAR_ACCREDITATION),
            getSiteSettings({
                params: {
                    keys: [
                        'accreditation_first_screen_title',
                        'accreditation_first_screen_desription',
                        'accreditation_first_screen_link',
                        'accreditation_first_screen_link_text',
                        'accreditation_accreditation_title',
                        'accreditation_accreditation_desription',
                        'accreditation_accreditation_link',
                        'accreditation_accreditation_link_text',
                        'accreditation_scheme_title',
                        'accreditation_scheme_document',
                        'accreditation_contact_title',
                        'accreditation_contact_desription',
                    ],
                },
            }),
            getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.CAR_ACCREDITATION } }),
            getCarAccreditation(),
            getSchemes({ params: { page: pageResponse.data?.id ?? PAGES.CAR_ACCREDITATION } }),
        ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'services-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Сервисы', href: '/services/' },
            ],
            h1: pageResponse.data?.meta?.title || 'Сервисы',
            pageTitle: siteSettings.data?.accreditation_first_screen_title || 'Сервисы',
            titleDescription:
                siteSettings.data?.accreditation_first_screen_desription ||
                'Используйте возможности сервисов, чтобы сделать ваше участие в событиях TNF максимально продуктивным',

            buttonText: siteSettings.data?.accreditation_first_screen_link_text || 'Служба поддержки',
            buttonLink: siteSettings.data?.accreditation_first_screen_link || '',
            navigation: [
                {
                    id: 0,
                    title: 'Бронирование гостиниц',
                    href: '/services/hotels/',
                },
                {
                    id: 1,
                    title: 'Аренда транспорта',
                    href: '/services/car-rent/',
                },
                {
                    id: 2,
                    title: 'Аккредитация транспорта',
                    href: '/services/car-accreditation/',
                },
                {
                    id: 3,
                    title: 'Шаттлы',
                    href: '/services/shuttles/',
                },
                {
                    id: 4,
                    title: 'Зоны питания',
                    href: '/services/food-areas/',
                },
            ],

            servicesContentHeaderProps: {
                title: siteSettings.data?.accreditation_accreditation_title || 'Аккредитация автомобиля',
                description:
                    siteSettings.data?.accreditation_accreditation_desription ||
                    'Дополнительно можно аккредитовать автомобиль с правом парковки на территории Технопарка или с правом транзитного проезда по стоимости',
                link: siteSettings.data?.accreditation_accreditation_link
                    ? {
                          href: siteSettings.data.accreditation_accreditation_link,
                          name: siteSettings.data.accreditation_accreditation_link_text || 'Подать заявку',
                      }
                    : null,
            },

            carAccreditationContentProps: {
                cards: carAccreditationResponse.data || [],
            },

            schemaSectionProps: {
                title: siteSettings.data?.accreditation_scheme_title || '',
                documents: siteSettings.data?.accreditation_scheme_document?.src
                    ? [
                          {
                              src: siteSettings.data.accreditation_scheme_document.src,
                              title: siteSettings.data.accreditation_scheme_document.title ?? '',
                              alt: siteSettings.data.accreditation_scheme_document.alt ?? '',
                          },
                      ]
                    : [],
                schemas: schemesResponse.data || [],
            },

            additionalInfoProps: {
                title: siteSettings.data?.accreditation_contact_title || 'Дополнительная информация',
                subtitle:
                    siteSettings.data?.accreditation_contact_desription ||
                    'Получить дополнительную информацию об условиях бронирования можно у оператора Форума в сфере гостеприимства - компании Promo Travel:',
                contacts: employeesResponse.data || [],
            },
        } satisfies ServicesPageProps,
        revalidate: 60,
    };
};
